import { useState, useEffect } from 'react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';

/**
 * TableBuilderInput Component
 * Renders a dynamic table builder for TableBuilder type placeholders
 * Supports both horizontal (headers top) and vertical (headers left) layouts
 *
 * @param {string} label - Title of the table
 * @param {string} description - Comma-separated column/row headers from admin
 * @param {string} orientation - 'horizontal' (default) or 'vertical'
 * @param {object} value - Current value { headers: [], rows: [[]], orientation: string }
 * @param {function} onChange - Callback when value changes
 */
export default function TableBuilderInput({ label, description, orientation = 'horizontal', value, onChange, onFocus }) {
    const { t } = useTranslation();

    const parseHeaders = (desc) => {
        if (!desc) return [];
        return desc.split(',').map(h => h.trim()).filter(h => h.length > 0);
    };

    const headers = parseHeaders(description);
    const headerCount = headers.length;
    const isVertical = orientation === 'vertical';

    const [data, setData] = useState(() => {
        if (value?.rows && value.rows.length > 0) return value.rows;
        return [];
    });

    useEffect(() => {
        if (value?.rows) {
            const isEmpty = value.rows.length === 0 ||
                (value.rows.length === 1 && value.rows[0].length === 0);
            if (isEmpty) {
                setData([]);
            } else if (JSON.stringify(value.rows) !== JSON.stringify(data)) {
                setData(value.rows);
            }
        } else {
            setData([]);
        }
    }, [value?.rows]);

    useEffect(() => {
        if (headerCount > 0) {
            const newData = data.map(row => {
                if (row.length < headerCount) return [...row, ...Array(headerCount - row.length).fill('')];
                if (row.length > headerCount) return row.slice(0, headerCount);
                return row;
            });
            if (JSON.stringify(newData) !== JSON.stringify(data)) {
                setData(newData);
                updateValue(headers, newData);
            }
        }
    }, [headerCount, isVertical]);

    const updateValue = (newHeaders, newData) => {
        onChange({ headers: newHeaders, rows: newData, orientation });
    };

    const addItem = () => {
        const newRow = Array(headerCount).fill('');
        const newData = [...data, newRow];
        setData(newData);
        updateValue(headers, newData);
    };

    const removeItem = (index) => {
        const newData = data.filter((_, i) => i !== index);
        setData(newData);
        updateValue(headers, newData);
    };

    const updateCell = (rowIndex, colIndex, val) => {
        const newData = data.map((item, rIdx) => {
            if (rIdx === rowIndex) {
                const newItem = [...item];
                newItem[colIndex] = val;
                return newItem;
            }
            return item;
        });
        setData(newData);
        updateValue(headers, newData);
    };

    const cellInputClass = "w-full px-2 py-1 text-sm bg-transparent border border-transparent rounded focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-colors text-gray-900 dark:text-gray-100";
    const thClass = "px-2 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border-b border-r border-gray-200 dark:border-gray-700 text-left";
    const tdClass = "px-1 py-0.5 border-b border-r border-gray-200 dark:border-gray-700";

    if (headerCount === 0) {
        return (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4" onFocus={onFocus}>
                <div className="flex flex-col gap-2">
                    <span className="font-medium text-sm text-gray-800 dark:text-gray-100">{label}</span>
                    <span className="text-sm text-gray-400 dark:text-gray-500">{t('tableBuilder.noHeadersDefined')}</span>
                </div>
            </div>
        );
    }

    if (isVertical) {
        return (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4" onFocus={onFocus}>
                <div className="flex flex-col gap-3">
                    <span className="font-medium text-sm text-gray-800 dark:text-gray-100">{label}</span>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 dark:border-gray-700 text-sm">
                            <tbody>
                                {headers.map((header, headerIndex) => (
                                    <tr key={`header-row-${headerIndex}`}>
                                        <th className={`${thClass} w-[30%]`}>{header}</th>
                                        {data.length === 0 ? (
                                            <td className={`${tdClass} text-center text-gray-400 dark:text-gray-500`}>
                                                {headerIndex === 0 ? (t('tableBuilder.clickToAddRow') || 'Click + to add') : ''}
                                            </td>
                                        ) : (
                                            data.map((column, colIndex) => (
                                                <td key={`cell-${headerIndex}-${colIndex}`} className={tdClass}>
                                                    <input
                                                        type="text"
                                                        value={column[headerIndex] || ''}
                                                        onChange={(e) => updateCell(colIndex, headerIndex, e.target.value)}
                                                        className={cellInputClass}
                                                    />
                                                </td>
                                            ))
                                        )}
                                    </tr>
                                ))}
                                {data.length > 0 && (
                                    <tr>
                                        <td className="px-2 py-1 bg-gray-50 dark:bg-gray-800 border-b border-r border-gray-200 dark:border-gray-700" />
                                        {data.map((_, colIndex) => (
                                            <td key={`delete-${colIndex}`} className={`${tdClass} text-center`}>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(colIndex)}
                                                    title={t('tableBuilder.removeColumn') || 'Remove Column'}
                                                    className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                >
                                                    <IconTrash size={14} />
                                                </button>
                                            </td>
                                        ))}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <button
                        type="button"
                        onClick={addItem}
                        title={t('tableBuilder.addColumn') || 'Add Column'}
                        className="ml-auto flex items-center justify-center w-6 h-6 rounded bg-green-500 hover:bg-green-600 text-white transition-colors"
                    >
                        <IconPlus size={14} />
                    </button>
                </div>
            </div>
        );
    }

    // Horizontal layout (default)
    return (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4" onFocus={onFocus}>
            <div className="flex flex-col gap-3">
                <span className="font-medium text-sm text-gray-800 dark:text-gray-100">{label}</span>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 dark:border-gray-700 text-sm">
                        <thead>
                            <tr>
                                {headers.map((header, i) => (
                                    <th key={`header-${i}`} className={thClass}>{header}</th>
                                ))}
                                <th className="w-10 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr>
                                    <td colSpan={headers.length + 1} className="px-3 py-3 text-center text-xs text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-700">
                                        {t('tableBuilder.clickToAddRow') || 'Click + to add a row'}
                                    </td>
                                </tr>
                            ) : (
                                data.map((row, rowIndex) => (
                                    <tr key={`row-${rowIndex}`}>
                                        {row.map((cell, colIndex) => (
                                            <td key={`cell-${rowIndex}-${colIndex}`} className={tdClass}>
                                                <input
                                                    type="text"
                                                    value={cell}
                                                    onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                                                    className={cellInputClass}
                                                />
                                            </td>
                                        ))}
                                        <td className={`${tdClass} text-center w-10`}>
                                            <button
                                                type="button"
                                                onClick={() => removeItem(rowIndex)}
                                                title={t('tableBuilder.removeRow')}
                                                className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            >
                                                <IconTrash size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <button
                    type="button"
                    onClick={addItem}
                    title={t('tableBuilder.addRow')}
                    className="ml-auto flex items-center justify-center w-6 h-6 rounded bg-green-500 hover:bg-green-600 text-white transition-colors"
                >
                    <IconPlus size={14} />
                </button>
            </div>
        </div>
    );
}
